// The region's division-naming rule, in the one place every surface can reach.
//
// ⚠️ THIS LIVES HERE BECAUSE THE ALTERNATIVE WAS A LAYERING INVERSION. The rule
// was defined in `apps/workers/src/lib/volunteerAudienceSegments.ts` — a Campaign
// Monitor module — and `dao/practiceOptimize.ts` imported it from there, so the
// practice solver resolved a team's age through email-audience code. Moving it up
// is what lets both read it from a module that owns neither concern.
//
// The once-remaining copy is gone: `apps/referee`'s `dao/refereeSlots.ts` used
// to carry the same regex under the name `ageFromDivisionName`. It moved into
// `refereeWindows.ts` (this package) on 2026-09-08, alongside `isYoungDivision`
// and `slotIsOptional`, which both need the same age-from-name fallback.

/** PURE. `B10` → 10, and the older `B10U` and a bare `10U` read the same. Null
 *  when the name does not follow the region's `<gender><age>` convention (`EPIC`,
 *  `VIP`), which is a signal to leave the division alone rather than to guess at
 *  it. The trailing `U` is optional because stored division names are moving to
 *  InLeague's `B10` form (#881); until then both spellings are in the data.
 *
 *  ⚠️ NOT the same function as `apps/workers/src/dao/inleague-import.ts`'s
 *  `divisionAge`, which shares the name but parses INLEAGUE's spelling (`B6`,
 *  with no `U`) off a division object. Ours reads a division NAME in either
 *  spelling. The collision is a trap; check which you hold before reaching for
 *  either. Twinned with Python `src.core.designation.division_age`. */
export function divisionAge(division: string | null | undefined): number | null {
  const m = /^[A-Za-z]*(\d{1,2})U?$/.exec((division ?? '').trim())
  return m ? Number(m[1]) : null
}

/** PURE. `B10` / `B10U` → 'B', `G6U` → 'G'. Null for a name with no gender
 *  letter before its age (`EPIC`, `VIP`, `10U`). */
export function divisionGender(division: string | null | undefined): 'B' | 'G' | null {
  const m = /^([BG])\d/i.exec((division ?? '').trim())
  return m ? (m[1]!.toUpperCase() as 'B' | 'G') : null
}

/** PURE. Age from a TEAM designation or a division name carrying a suffix:
 *  `B10-01` and `B10U01` → 10, `B10UX` → 10, `G6U` → 6, `6U` → 6. Reads what
 *  precedes the first `U` or `-`, so the old and the InLeague spelling of a team
 *  id give one answer (#881). Null when no age leads (`EPIC`, '', null). */
export function designationAge(designation: string | null | undefined): number | null {
  return divisionAge((designation ?? '').trim().split(/[-uU]/)[0] ?? '')
}

/** PURE. The practice subsystem's ungendered age-group key: `B10U` and `B10` →
 *  `10U`, which is what `practice_division_config` is keyed by. A name with no
 *  gender letter passes through unchanged. */
export function divisionAgeGroup(division: string): string {
  if (divisionGender(division) === null) return division
  const age = divisionAge(division)
  return age === null ? division.slice(1) : `${age}U`
}

/** Ages that staff nobody. 4U/5U is parent-run playground soccer: no assigned
 *  coach, no referee, no team manager (owner ruling, 2026-08-01).
 *
 *  Two consumers, and they must not drift: `volunteerAudienceSegments` builds no
 *  per-division segment for these ages, and `isPreschoolOnly` withholds a
 *  volunteer who asked for nothing else (2026-08-15).
 *
 *  Related but deliberately separate: `apps/workers/src/lib/practiceFormGate.ts`
 *  writes the same 4/5-versus-6 boundary as `YOUNGEST_PRACTICING_AGE = 6`. It is
 *  a different question (does this age practice) that happens to fall the same
 *  way, so it is not derived from this — if preschool ever gains a coach, only
 *  one of the two should move. */
export const UNSTAFFED_AGES: ReadonlySet<number> = new Set([4, 5])

/** PURE. Does this division staff coaches, managers and referees?
 *
 *  ⚠️ FAILS OPEN: an unparseable name counts as staffed. For segments a spare
 *  empty one is clutter while three missing ones is a send that reaches nobody;
 *  for the audience gate, keeping somebody we cannot classify beats silently
 *  unsubscribing them because a division was renamed. */
export function hasStaffingRoles(division: string): boolean {
  const age = divisionAge(division)
  return age === null || !UNSTAFFED_AGES.has(age)
}

/** The youngest competitive age: 10U and up play repeat-free counting games. */
export const COMPETITIVE_AGE = 10

/** PURE. Is this a competitive (10U+) division by its name?
 *
 *  ⚠️ FAILS CLOSED, unlike `hasStaffingRoles`: an unparseable name is not
 *  competitive, so nothing is held to a 10U+ rule on a guess. By name only; the
 *  generator's `is_competitive` (`src/optimization/generation.py`) also exempts
 *  a division set to round-robin scheduling mode. */
export function isCompetitiveDivision(division: string): boolean {
  return (divisionAge(division) ?? 0) >= COMPETITIVE_AGE
}
